import {AlertDialogComponent} from "./_components/shared/alert-dialog/alert-dialog.component"
import {ApiErrorComponent} from "./_components/shared/api-error/api-error.component"
import {AppComponent} from "./app.component"
import {AppRoutingModule} from "./app-routing.module"
import {ApproveDecisionModalComponent} from "./_components/shared/approve-decision-modal/approve-decision-modal.component"
import {AuthenticationService} from "./_services/user/authentication/authentication.service"
import {AuthInterceptor} from "./_helpers/interceptors/auth.interceptor"
import {BasicAuthInterceptor, ErrorInterceptor} from "./_helpers"
import {BrowserAnimationsModule} from "@angular/platform-browser/animations"
import {BrowserModule} from "@angular/platform-browser"
import {CommonModule, CurrencyPipe, DatePipe, DecimalPipe} from "@angular/common"
import {ConfigService} from "./_services/config.service"
import {ConfirmDialogComponent} from "./_components/shared/confirm-dialog/confirm-dialog.component"
import {DateStringToUnixPipe} from "./_helpers/pipes/date-string-to-unix.pipe"
import {DragDropModule} from "@angular/cdk/drag-drop"
import {EndpointFactoryService} from "./_services/user/endpoint-factory.service"
import {FooterComponent} from "./_components/shared/footer/footer.component"
import {FormsModule, ReactiveFormsModule} from "@angular/forms"
import {GaeStaffReportComponent} from "./gae-staff-report/gae-staff-report.component"
import {HeaderComponent} from "./_components/shared/header/header.component"
import {HighchartsChartModule} from "highcharts-angular"
import {HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi} from "@angular/common/http"
import {IrwinToolPresentationComponent} from "./support-dashboard/irwin-tool-presentation/irwin-tool-presentation.component"
import {IrwinToolSmartComponent} from "./support-dashboard/irwin-tool-smart/irwin-tool-smart.component"
import {LinkConfigurationComponent} from "./support-dashboard/link-configuration-component/link-configuration-component.component"
import {LoadingComponent} from "./_components/shared/loading/loading.component"
import {LoadingInterceptor} from "./_helpers/interceptors/loading.interceptor"
import {LoginComponent} from "./_components/shared/login/login.component"
import {map, catchError, tap} from "rxjs/operators"
import {mapLayerTitles} from "src/app/_models/map/map-layers/map-layer-titles"
import {MatAutocompleteModule} from "@angular/material/autocomplete"
import {MatButtonModule} from "@angular/material/button"
import {MatCardModule} from "@angular/material/card"
import {MatCheckboxModule} from "@angular/material/checkbox"
import {MatDatepickerModule} from "@angular/material/datepicker"
import {MatDialogModule} from "@angular/material/dialog"
import {MatDividerModule} from "@angular/material/divider"
import {MatExpansionModule} from "@angular/material/expansion"
import {MatFormFieldModule} from "@angular/material/form-field"
import {MatGridListModule} from "@angular/material/grid-list"
import {MatIconModule} from "@angular/material/icon"
import {MatInputModule} from "@angular/material/input"
import {MatListModule} from "@angular/material/list"
import {MatMenuModule} from "@angular/material/menu"
import {MatPaginatorModule} from "@angular/material/paginator"
import {MatProgressBarModule} from "@angular/material/progress-bar"
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner"
import {MatRadioModule} from "@angular/material/radio"
import {MatSelectModule} from "@angular/material/select"
import {MatSliderModule} from "@angular/material/slider"
import {MatSnackBarModule} from "@angular/material/snack-bar"
import {MatSortModule} from "@angular/material/sort"
import {MatStepperModule} from "@angular/material/stepper"
import {MatTableModule} from "@angular/material/table"
import {MatTabsModule} from "@angular/material/tabs"
import {MatToolbarModule} from "@angular/material/toolbar"
import {MatTooltipModule} from "@angular/material/tooltip"
import {MimicRolesComponent} from "./support-dashboard/mimic-roles/mimic-roles.component"
import {NgModule, inject, provideAppInitializer} from "@angular/core"
import {NotificationsComponent} from "./_components/notifications/notifications.component"
import {of, Observable, ObservableInput, from, concat, forkJoin} from "rxjs"
import {RTEModule} from "./_modules/rte/rte.module"
import {SavedInterceptor} from "./_helpers/interceptors/saved.interceptor"
import {SharedModule} from "./_modules/shared/shared.module"
import {SsoInitiationComponent} from "./_components/auth/sso-initiation/sso-initiation.component"
import {SsoResponseHandlerComponent} from "./_components/auth/sso-response-handler/sso-response-handler.component"
import {SupportDashboardComponent} from "./support-dashboard/support-dashboard.component"
import {MapLayersService} from "./_services/map/map-layers/map-layers.service"
import {MapLayer} from "./_models/map/map-layers/map-layer.model"

/**
 * @description This function assigned in the app.module provideAppIntiializer runs before the app loads anything else.
 * It populates environment specific global values from externalized files. With this setup those values can be updated dynamically per environment without rebuild.
 * Currently, this function provides the following in order via the forkJoin operator:
 * 1.) populate the configservice with endpoint/server urls specific to this environment
 * 2.) attach the map-layers.js script to the dom. map-layers.js provides the esri-map-service with the map layers/urls, also specific to this environment
 * 3.) After the forkJoin completes(therefore the config and addMapLayersScript completed as well), use the now available getMapLayersArray and configService to populate esri-map-service with the map layers.
 * @param http
 * @param configService
 * @param mapLayerService
 * @returns
 */
function appInitializer(http: HttpClient, configService: ConfigService, mapLayerService: MapLayersService): Observable<any> {
	return forkJoin([
		http.get<ConfigService>("./assets/envs/config.json").pipe(
			tap((configFromJson: ConfigService) => {
				configService.name = configFromJson.name
				configService.label = configFromJson.label
				configService.irwin_version = configFromJson.irwin_version
				configService.arcGisServerHost = configFromJson.arcGisServerHost
				configService.arcGisServerEndpoint = configFromJson.arcGisServerEndpoint
				configService.mapServicePrefix = configFromJson.mapServicePrefix
				configService.femsEndpoint = configFromJson.femsEndpoint
				configService.esriTZEndpoint = configFromJson.esriTZEndpoint
				configService.baseBackendUrl = configFromJson.baseBackendUrl
				configService.userApi = configFromJson.userApi
				configService.caseApi = configFromJson.caseApi
				configService.decisionApi = configFromJson.decisionApi
				configService.caseApi.getLatestStrategicAssessment = (caseId) => `/cases/${caseId}/latestStrategicAssessment`
				configService.caseApi.getLatestStrategicAssessmentIemPriorities = (caseId, assessmentId) => `/cases/${caseId}/strategicAssessmentItems/priority-order/asc`
				configService.caseApi.putLatestStrategicAssessment = (caseId) => `/cases/${caseId}/latestStrategicAssessment`
				configService.caseApi.createNewStrategicAssessmentItem = (caseId) => `/cases/${caseId}/newStrategicAssessmentItem`
				configService.caseApi.getLatestStrategicAssessmentItems = (caseId) => `/cases/${caseId}/latestStrategicAssessmentItems`
				configService.caseApi.putStrategicAssessmentItem = (caseId, assessmentItemId) => `/cases/${caseId}/assessmentItem/${assessmentItemId}`
				configService.caseApi.putStrategicAssessmentItemPriorities = (caseId, assessmentId) =>
					`/cases/${caseId}/strategicAssessment/${assessmentId}/updateStrategicAssessmentItemPriorities`
				configService.caseApi.deleteStrategicAssessmentItem = (caseId, assessmentItemId) => `/cases/${caseId}/assessmentItem/${assessmentItemId}`
				configService.caseApi.putCurrentPlanningAreaCreatedDate = (caseId) => `/cases/${caseId}/currentPlanningAreaCreatedDate`
				configService.irwinApi = configFromJson.irwinApi
				configService.commonUtilityApi = configFromJson.commonUtilityApi
				configService.analysisServiceApi = configFromJson.analysisServiceApi
				configService.geoprocessingApi = configFromJson.geoprocessingApi
				configService.auth = configFromJson.auth
			}),
			catchError((err, caught) => {
				console.error(
					"SEVERE UI ERROR: config.json failed to load/initialize! The application cannot load until this is remedied. Contact a developer immediately.",
				)
				return of()
			}),
		),
		from(addMapLayersScript).pipe(
			catchError((err, caught) => {
				console.error("MAJOR UI ERROR: map-layers.js failed to load/initialize! The map will not function until this is remedied. Contact a developer.")
				return of()
			}),
		),
	]).pipe(
		tap((x) => {
			mapLayerService.setMapLayers(getMapLayersArray(configService, mapLayerTitles))
		}),
	)
}

const addMapLayersScript = new Promise<void>((resolve, reject) => {
	const script = document.createElement("script")
	script.type = "text/javascript"
	script.src = "assets/js/map-layers.js"
	script.async = true
	script.onload = () => {
		resolve()
	}
	script.onerror = (error) => {
		reject(`Failed to read map layer script`)
	}
	document.body.appendChild(script)
})

declare function getMapLayersArray(environment: ConfigService, mapLayerTitles: Object): MapLayer[]
@NgModule({
	declarations: [
		AlertDialogComponent,
		ApiErrorComponent,
		AppComponent,
		ApproveDecisionModalComponent,
		ConfirmDialogComponent,
		DateStringToUnixPipe,
		FooterComponent,
		GaeStaffReportComponent,
		HeaderComponent,
		IrwinToolPresentationComponent,
		IrwinToolSmartComponent,
		LinkConfigurationComponent,
		LoadingComponent,
		LoginComponent,
		MimicRolesComponent,
		NotificationsComponent,
		SsoInitiationComponent,
		SsoResponseHandlerComponent,
		SupportDashboardComponent,
	],
	bootstrap: [AppComponent],
	exports: [RTEModule],
	imports: [
		AppRoutingModule,
		BrowserAnimationsModule,
		BrowserModule,
		CommonModule,
		DragDropModule,
		FormsModule,
		HighchartsChartModule,
		MatAutocompleteModule,
		MatButtonModule,
		MatCardModule,
		MatCheckboxModule,
		MatDatepickerModule,
		MatDialogModule,
		MatDividerModule,
		MatExpansionModule,
		MatFormFieldModule,
		MatGridListModule,
		MatIconModule,
		MatInputModule,
		MatListModule,
		MatMenuModule,
		MatPaginatorModule,
		MatProgressBarModule,
		MatProgressSpinnerModule,
		MatRadioModule,
		MatSelectModule,
		MatSliderModule,
		MatSnackBarModule,
		MatSortModule,
		MatStepperModule,
		MatTableModule,
		MatTabsModule,
		MatToolbarModule,
		MatTooltipModule,
		ReactiveFormsModule,
		RTEModule,
		SharedModule,
	],
	providers: [
		EndpointFactoryService,
		DatePipe,
		CurrencyPipe,
		DecimalPipe,
		{provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true},
		{provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true},
		{provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true},
		{provide: HTTP_INTERCEPTORS, useClass: SavedInterceptor, multi: true},
		provideAppInitializer(() => {
			return appInitializer(inject(HttpClient), inject(ConfigService), inject(MapLayersService))
		}),
		provideHttpClient(withInterceptorsFromDi()),
	],
})
export class AppModule {}

/**Below is the original 'configservice' app initializer, which was in place from approximately 9.1.24 to 8.1.26
 * Retaining here for reference temporarily while we battle test the new implementation.
 * Please delete this if you're reading this 6 months+ in the future and the new implementation works so well that you have never even heard of this funtionality.
 * */
// function initializeApp(http: HttpClient, config: ConfigService, auth: AuthenticationService): (() => Promise<boolean>) {
//   return (): Promise<boolean> => {
//     return new Promise<boolean>((resolve: (a: boolean) => void): void => {
//       setTimeout(() => resolve(true), 10000);
//       http.get<ConfigService>('./assets/envs/config.json')
//         .pipe(
//           map((x: ConfigService) => {
//             config.name = x.name;
//             config.label = x.label;
//             config.irwin_version = x.irwin_version;
//             config.arcGisServerHost = x.arcGisServerHost;
//             config.arcGisServerEndpoint = x.arcGisServerEndpoint;
//             config.mapServicePrefix = x.mapServicePrefix;
//             config.femsEndpoint = x.femsEndpoint;
//             config.esriTZEndpoint = x.esriTZEndpoint;
//             config.baseBackendUrl = x.baseBackendUrl;
//             config.userApi = x.userApi;
//             config.caseApi = x.caseApi;
//             config.decisionApi = x.decisionApi;
//             config.caseApi.getLatestStrategicAssessment = (caseId) => `/cases/${caseId}/latestStrategicAssessment`;
//             config.caseApi.getLatestStrategicAssessmentIemPriorities = (caseId, assessmentId) => `/cases/${caseId}/strategicAssessmentItems/priority-order/asc`;
//             config.caseApi.putLatestStrategicAssessment = (caseId) => `/cases/${caseId}/latestStrategicAssessment`;
//             config.caseApi.createNewStrategicAssessmentItem = (caseId) => `/cases/${caseId}/newStrategicAssessmentItem`;
//             config.caseApi.getLatestStrategicAssessmentItems = (caseId) => `/cases/${caseId}/latestStrategicAssessmentItems`;
//             config.caseApi.putStrategicAssessmentItem = (caseId, assessmentItemId) => `/cases/${caseId}/assessmentItem/${assessmentItemId}`;
//             config.caseApi.putStrategicAssessmentItemPriorities = (caseId, assessmentId) => `/cases/${caseId}/strategicAssessment/${assessmentId}/updateStrategicAssessmentItemPriorities`;
//             config.caseApi.deleteStrategicAssessmentItem = (caseId, assessmentItemId) => `/cases/${caseId}/assessmentItem/${assessmentItemId}`;
//             config.caseApi.putCurrentPlanningAreaCreatedDate = (caseId) => `/cases/${caseId}/currentPlanningAreaCreatedDate`;
//             config.irwinApi = x.irwinApi;
//             config.commonUtilityApi = x.commonUtilityApi;
//             config.analysisServiceApi = x.analysisServiceApi;
//             config.geoprocessingApi = x.geoprocessingApi;
//             config.auth = x.auth;
//             resolve(true);
//           }),
//           // In case you get a 404 error, load the config with default values.  Only place that might happen is using ng serve during development
//           catchError((x: { status: number }, caught: Observable<void>): ObservableInput<{}> => {
//             if (x.status !== 404) {
//               resolve(false);
//             }
//             config.name = "dev";
//             config.label = "default-env";
//             config.irwin_version = "OAT_NEXT_IRWIN_Incidents";
//             config.arcGisServerHost = "https://ip-10-32-10-253.us-west-2.compute.internal:6443";
//             config.arcGisServerEndpoint = 'https://ip-10-32-10-253.us-west-2.compute.internal:6443/arcgis/rest/services/WFDSS';
//             config.mapServicePrefix = "Testing_WFMRDA";
//             config.femsEndpoint = "https://fems-stg.fs2c.usda.gov/api/climatology/graphql";
//             config.baseBackendUrl = "https://wfdssdev.firenet.gov";
//             config.esriTZEndpoint = "https://services.arcgis.com/P3ePLMYs2RVChkJx/ArcGIS/rest/services/World_Time_Zones/FeatureServer/0"
//             config.userApi = {
//               baseUrl: "https://wfdssdev.firenet.gov/userservice/user",
//               basicSearch: "/usersByBasicSearch",
//               advancedUserSearch: "/usersBySearch",
//               login: "/login",
//               getList: "/getList",
//               isCertifiedApprover: "/isCertifiedApprover",
//               gaeStaffReport: "/editorGeographicAreas",
//             };
//             config.caseApi = {
//               baseUrl: "https://wfdssdev.firenet.gov/caseservice",
//               caseByName: "/casesByName",
//               cases: "/cases",
//               collaborators: "/collaborators",
//               caseHistoryRecords: "/caseHistoryRecords",

//               getLatestStrategicAssessment: (caseId) => `/cases/${caseId}/strategicAssessment`,
//               getLatestStrategicAssessmentIemPriorities: (caseId, assessmentId) => `/cases/${caseId}/strategicAssessmentItems/priority-order/asc`,
//               putLatestStrategicAssessment: (caseId) => `/cases/${caseId}/latestStrategicAssessment`,
//               createNewStrategicAssessmentItem: (caseId) => `/cases/${caseId}/newStrategicAssessmentItem`,
//               getLatestStrategicAssessmentItems: (caseId) => `/cases/${caseId}/latestStrategicAssessmentItems`,
//               putStrategicAssessmentItem: (caseId, assessmentItemId) => `/cases/${caseId}/assessmentItem/${assessmentItemId}`,
//               putStrategicAssessmentItemPriorities: (caseId, assessmentItemId) => `/cases/${caseId}/strategicAssessment/${assessmentItemId}/updateStrategicAssessmentItemPriorities`,
//               deleteStrategicAssessmentItem: (caseId, assessmentItemId) => `/cases/${caseId}/assessmentItem/${assessmentItemId}`,
//               putCurrentPlanningAreaCreatedDate: (caseId) => `/cases/${caseId}/currentPlanningAreaCreatedDate`,
//             };
//             config.irwinApi = {
//               baseUrl: "https://wfdssdev.firenet.gov/irwinservice",
//               basicSearch: "/incidentsByBasicSearch",
//               getOrCreateWfdssCase: "/getOrCreateWfdssCase",
//               incidents: "/incidents",
//               updateAttribute: "/updateIrwinAttribute",
//               updateAttributes: "/updateIrwinAttributes"
//             };
//             config.commonUtilityApi = {
//               baseUrl: "https://wfdssdev.firenet.gov/commonutilityservice",
//               mockErrorUrl: "/mockError",
//               jurisdictionalAgencies: "/jurisdictionalAgencies",
//               geographicAreas: "/geographicAreas",
//               units: "/units",
//             };
//             config.analysisServiceApi = {
//               baseUrl: "https://wfdssdev.firenet.gov/analysisservice",
//               analysisUrlFragment: "/analysis",
//             };
//             config.geoprocessingApi = {
//               baseUrl: "/arcgis/rest/services/WFDSS_GP",
//               exportFeatures: "/exportFeatures2/GPServer/Export%20Features",
//               updateCOA: "/UpdateCOA/GPServer/Update%20COA",
//               printService: "/ExportWebMap/GPServer/Export%20Web%20Map"
//             };
//             config.decisionApi = {
//               baseUrl: "https://wfdssdev.firenet.gov/decisionservice",
//             };
//             config.auth = {
//               oauthBaseUrl: "https://famauth-qa.wildfire.gov",
//               authFlowInitiationUrl: "http://localhost:4200/auth/sso",
//               postLoginRedirectUrl: "http://localhost:4200/auth/validate",
//               clientId: "WFDSSNG-DEV",
//               idTokenUrl: "https://wfdssdev.firenet.gov/userservice/authn/idTokenByCode",
//               refreshTokenUrl:
//                 "https://wfdssdev.firenet.gov/userservice/authn/idTokenByRefreshToken",
//               isLocal: true,
//             };
//             resolve(true);
//             return of ({});
//           })
//         ).subscribe();
//     })
//   }
// }
